import React, { useEffect, startTransition } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { activeTabAtom } from '../../../../atoms/v3/uiAtoms';
import { IOSTabBar } from './IOSTabBar';
import { UndoToastContainer } from '../../../../components/v3/UndoToastContainer';
import { ToastContainer } from '../../../../components/v3/ToastContainer';
import { updateTaskAtom } from '../../../../atoms/v0/taskAtoms';
import {
  ChecklistIcon,
  ChecklistIconFilled,
  CalendarIcon,
  CalendarIconFilled,
  ChatIcon,
  ChatIconFilled,
  UserIcon,
  UserIconFilled,
  FocusIcon,
  FocusIconFilled,
  TaskV2Icon,
  TaskV2IconFilled,
  ProjectIcon,
  ProjectIconFilled
} from './IOSIcons';
import { trackPageView } from '../../../../lib/analytics';
import { trackNavigationEvents } from '../../../../lib/analyticsEnhanced';
import { useIOSViewportFix } from '../../../../hooks/v3/useIOSViewportFix';
import { globalKeymapModalOpenAtom } from '../../../../atoms/v3/uiAtoms';
import { KeymapModal } from '../tasks/KeymapModal';

export const IOSLayout: React.FC = () => {
  useIOSViewportFix();
  const location = useLocation();
  const navigate = useNavigate();
  const activeTabState = useAtomValue(activeTabAtom);
  const [, updateTask] = useAtom(updateTaskAtom);
  const { t } = useTranslation(['v3/tasks', 'v3/common']);

  // 快捷键帮助弹窗状态（现在由 useShortcutManager 统一管理）
  const [isKeymapModalOpen, setIsKeymapModalOpen] = useAtom(globalKeymapModalOpenAtom);

  // 获取当前页面类型
  const getCurrentPageType = () => {
    const path = location.pathname;
    if (path.includes('/v3/taskv2')) return 'taskv2';
    if (path.includes('/v3/projectv2')) return 'projectv2';
    if (path.includes('/v3/chat')) return 'chat';
    if (path.includes('/v3/me')) return 'me';
    return 'unknown';
  };
  const currentPageType = getCurrentPageType();
  // Store previous pathname to detect changes
  const prevPathnameRef = React.useRef(location.pathname);

  // 处理撤销操作
  const handleUndo = (taskId: string, previousState: boolean) => {
    updateTask({
      id: taskId,
      done: previousState
    });
  };

  useEffect(() => {
    if (location.pathname === prevPathnameRef.current && activeTabState !== null) {
      return;
    }

    const pathParts = location.pathname.split('/');
    const basePath = pathParts[2] || 'chat';
    let currentTabId = activeTabState;
    let pageName = basePath;

    if (basePath === 'projects') {
      pageName = 'project detail';
    }

    // 记录页面访问事件（原有）
    trackPageView(pageName, {
      path: location.pathname,
      tab: currentTabId || 'unknown'
    });

    // 记录增强的页面访问事件（移动端）
    trackNavigationEvents.pageViewed(pageName, {
      path: location.pathname,
      tab: currentTabId || 'unknown',
      layout_mode: 'mobile',
      previous_path: prevPathnameRef.current
    });

    // 如果是路由变化，记录路由变化事件
    if (prevPathnameRef.current && prevPathnameRef.current !== location.pathname) {
      trackNavigationEvents.routeChanged(prevPathnameRef.current, location.pathname, {
        layout_mode: 'mobile',
        tab: currentTabId || 'unknown'
      });
    }

    prevPathnameRef.current = location.pathname;

  }, [location.pathname, activeTabState]);



  // 标签配置
  const tabs = [
    {
      id: 'taskv2',
      label: t('v3/common:tabs.taskv2', '规划'),
      icon: <TaskV2Icon />,
      activeIcon: <TaskV2IconFilled />
    },
    {
      id: 'projectv2',
      label: t('v3/common:tabs.projectv2', '项目'),
      icon: <ProjectIcon />,
      activeIcon: <ProjectIconFilled />
    },
    {
      id: 'chat',
      label: t('v3/common:tabs.chat'),
      icon: <ChatIcon />,
      activeIcon: <ChatIconFilled />
    },
    {
      id: 'me',
      label: t('v3/common:tabs.me'),
      icon: <UserIcon />,
      activeIcon: <UserIconFilled />
    },
  ];

  return (
    <div className="flex flex-col h-screen bg-ios-gray6 dark:bg-ios-dark-gray6 font-ios">
      {/* 内容区域 - 移除状态栏和导航栏 */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>

      {/* 标签栏 */}
      <IOSTabBar tabs={tabs} />

      {/* 撤销 Toast 容器 */}
      <UndoToastContainer onUndo={handleUndo} />

      {/* 全局 Toast 容器 */}
      <ToastContainer />

      {/* 全局键盘快捷键说明弹窗 */}
      <KeymapModal
        isOpen={isKeymapModalOpen}
        onClose={() => setIsKeymapModalOpen(false)}
        currentPageType={currentPageType}
      />
    </div>
  );
};
